import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { VideoPlaybackAction, VideoPlaybackState } from "../../student-selection/videoplayer/ProctoringResultView";
import ProctoringVideoPlayer, {
	RecordingSourceVideoUrls,
	SessionNote,
	VideoSettings,
} from "../../student-selection/videoplayer/ProctoringVideoPlayer";
import useTimestamps, { TimestampsArr, VideoEvent } from "../../../utilities/useTimestamps";
import { useProctoredMediaContainer } from "../../student-selection/videoplayer/hooks";
import { VideoWrapper } from "../../student-selection/videoplayer/VideoControls/VideoControlLayout";

type LiveProctoringSessionNote = SessionNote & {
	id: string;
};

export default function VideoPlayer({
	sessionId,
	webcamVidSources = {},
	screenVidSources = {},
	duration,
	timeOffset = 0,
	startDate = new Date(0),
	audioOnly = false,
	hideCameraVideo = false,
	events = [],
	sessionEvents = [],
	liveNotes = [],
}: {
	sessionId: string;
	webcamVidSources?: RecordingSourceVideoUrls;
	screenVidSources?: RecordingSourceVideoUrls;
	duration?: number;
	timeOffset?: number;
	startDate?: Date;
	audioOnly?: boolean;
	hideCameraVideo?: boolean;
	events?: VideoEvent[];
	sessionEvents?: VideoEvent[];
	liveNotes?: LiveProctoringSessionNote[];
}) {
	const filterMatchingTimestampSources = (
		sources: RecordingSourceVideoUrls,
		timestamps: Record<number, TimestampsArr>,
	): RecordingSourceVideoUrls =>
		Object.fromEntries(
			Object.entries(sources)
				.map(([index, sourceUrls]) => {
					const timestampSourceKeys = new Set(timestamps[Number(index)]?.map(({ vid }) => String(vid)));
					return [
						Number(index),
						Object.fromEntries(Object.entries(sourceUrls).filter(([sourceKey]) => timestampSourceKeys.has(sourceKey))),
					];
				})
				.filter(([, sourceUrls]) => Object.keys(sourceUrls).length > 0),
		);

	const sessionRecordingTimestamps = useTimestamps(sessionEvents);
	const filteredWebcamSources = filterMatchingTimestampSources(webcamVidSources, sessionRecordingTimestamps.webcam);
	const filteredScreenSources = filterMatchingTimestampSources(
		screenVidSources,
		sessionRecordingTimestamps.screencapture,
	);

	const screenAvail = Object.values(filteredScreenSources).some((sources) => Object.keys(sources).length > 0);
	const webcamAvail = Object.values(filteredWebcamSources).some((sources) => Object.keys(sources).length > 0);
	const showVideoUnavailable = !screenAvail && !webcamAvail;

	const [webCamHasError, setWebCamHasError] = useState(false);
	const [screenCaptureHasError, setScreenCaptureHasError] = useState(false);
	useEffect(() => {
		setWebCamHasError(false);
	}, [webcamVidSources]);
	useEffect(() => {
		setScreenCaptureHasError(false);
	}, [screenVidSources]);

	const [inFullscreen, setInFullscreen] = useState(false);
	const [createdObserver, setCreatedObserver] = useState(false);

	const setFullscreen = useCallback(() => {
		// The page is not in an iframe
		if (!document.fullscreenElement) {
			void document.getElementById("entire_player")?.requestFullscreen();
			// setStringExtend("_fs");
			if (!createdObserver) {
				setCreatedObserver(true);
				makeObserver();
			}
		} else {
			void document.exitFullscreen();
		}
	}, [createdObserver]);

	// Makes an observer to check when the videos are in fullscreen or not.
	function makeObserver() {
		// Select the node that will be observed for mutations
		const targetNode = document.getElementById("entire_player");

		// Options for the observer (which mutations to observe)
		const config = { attributes: true, childList: true, subtree: true };

		// Callback function to execute when mutations are observed
		const callback = (mutationList: { type: string }[], _observer: MutationObserver) => {
			for (const mutation of mutationList) {
				if (mutation.type === "attributes") {
					if (document.fullscreenElement) {
						setInFullscreen(true);
					} else {
						setInFullscreen(false);
					}
				}
			}
		};

		// Create an observer instance linked to the callback function
		const observer = new MutationObserver(callback);

		// Start observing the target node for configured mutations
		if (targetNode) {
			observer.observe(targetNode, config);
		}
		// Later, you can stop observing
		// observer.disconnect();
	}

	const [controlOptions, setControlOptions] = useState({
		isPlaying: false,
		playbackSpeed: 1,
		volume: 0.5,
	});
	const reducer = (state: VideoPlaybackState, action: VideoPlaybackAction) => {
		if (
			duration &&
			((webcamAvail && !webCamHasError) ||
				(screenAvail && !screenCaptureHasError) ||
				(action.type === "skip" && action.time === timeOffset))
		) {
			switch (action.type) {
				case "play":
					if (state.timer < duration) {
						return {
							timer: state.timer + (performance.now() - state.lastTime) * action.speed,
							lastTime: performance.now(),
							skip: false,
						};
					} else {
						setControlOptions((controlOptions) => ({
							...controlOptions,
							isPlaying: !controlOptions.isPlaying,
						}));
						return { timer: state.timer, lastTime: performance.now(), skip: false };
					}
				case "pause":
					return { timer: state.timer, lastTime: performance.now(), skip: false };
				case "skip":
					return {
						timer: Math.max(timeOffset, Math.min(Math.round(action.time), duration)),
						lastTime: performance.now(),
						skip: true,
					};
			}
		}
		return state;
	};

	const [playback, playbackDispatch] = useReducer(reducer, { timer: 0, lastTime: performance.now(), skip: false });

	useEffect(() => {
		playbackDispatch({ type: "skip", time: timeOffset });
	}, [timeOffset]);

	useEffect(() => {
		if (controlOptions.isPlaying) {
			const interval = setInterval(() => {
				playbackDispatch({ type: "play", speed: controlOptions.playbackSpeed });
			}, 1000 / 60);
			return () => clearInterval(interval);
		}
	}, [controlOptions.isPlaying, controlOptions.playbackSpeed]);

	const setPlay = () => {
		// Used to reset the timer otherwise a paused video's time will continue to go up even when the video is paused (so unpausing it will cause a skip)
		// this will update the timer the second the video starts playing making sure that the calculations are correct (updating performance.now to the most recent time)
		playbackDispatch({ type: "pause" });
		setControlOptions((controlOptions) => ({
			...controlOptions,
			isPlaying: !controlOptions.isPlaying,
		}));
		const pauseImage = document.getElementById("pauseIconImage");
		setTimeout(() => {
			pauseImage?.setAttribute("style", "display: none");
		}, 250);
	};

	const setVolume: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		setControlOptions((controlOptions) => ({
			...controlOptions,
			volume: Number(e.target.value),
		}));
	};

	const setSpeed = (value: string) => {
		const num = parseFloat(value);
		setControlOptions((controlOptions) => ({
			...controlOptions,
			playbackSpeed: num,
		}));
	};

	useEffect(() => {
		const handleKeyboard = (event: KeyboardEvent) => {
			const playerElement = document.getElementById("entire_player");
			const focusedElement = document.activeElement;
			if (focusedElement?.closest(".MuiDialog-container") !== playerElement?.closest(".MuiDialog-container")) {
				return;
			}
			if (webCamHasError && screenCaptureHasError) {
				if (event.key === "f" || event.key === "F") {
					setFullscreen();
					return;
				}
			}
			if (event.key === " ") {
				if (focusedElement?.tagName.toLowerCase() !== "button") {
					setPlay();
				}
			} else if (event.key === "f") {
				setFullscreen();
			} else if (event.key === "ArrowRight") {
				playbackDispatch({ type: "skip", time: playback.timer + 10000 });
			} else if (event.key === "ArrowLeft") {
				playbackDispatch({ type: "skip", time: playback.timer - 10000 });
			}
		};
		document.addEventListener("keydown", handleKeyboard);
		return () => document.removeEventListener("keydown", handleKeyboard);
	}, [setFullscreen, playback.timer]);

	const [showFullscreenButtons, setShowFullscreenButtons] = useState(false);
	document.addEventListener("click", function handleClickOutsideBox(event) {
		if (showFullscreenButtons) {
			const button = document.getElementById("fullscreenModeButton");
			if (button?.contains(event.target as Node)) {
				return;
			} else {
				setShowFullscreenButtons(false);
			}
		}
	});

	const shouldShowDownload =
		(webcamVidSources[0] &&
			Object.keys(webcamVidSources[0]).length > 0 &&
			!Object.values(webcamVidSources[0]).some((source) => source === "processing")) ||
		(screenVidSources[0] &&
			Object.keys(screenVidSources[0]).length > 0 &&
			!Object.values(screenVidSources[0]).some((source) => source === "processing"));

	const visibleLiveNotes = liveNotes;

	const videoJsOptions = {
		controls: true,
		responsive: true,
		aspectRatio: "16:9",
		fluid: true,
		userActions: {
			click: false,
		},
		controlBar: {
			playToggle: false,
			volumePanel: false,
			progressControl: false,
			remainingTimeDisplay: false,
			pictureInPictureToggle: false,
			fullscreenToggle: false,
		},
		bigPlayButton: false,
	} as VideoSettings;

	const proctoredMediaContainer = useProctoredMediaContainer({
		webcam: webcamVidSources,
		screencapture: screenVidSources,
		roomScan: useMemo(() => ({}), []),
	});

	return (
		<VideoWrapper inFullscreen={inFullscreen} showTimestamps={false} useDoubleWrappers>
			<ProctoringVideoPlayer
				isFullscreen={inFullscreen}
				sessionId={sessionId}
				showTimestamps={false}
				controlOptions={controlOptions}
				playback={playback}
				playbackDispatch={playbackDispatch}
				sessionRecordingTimestamps={sessionRecordingTimestamps}
				videos={{
					webcam: filteredWebcamSources,
					screencapture: filteredScreenSources,
				}}
				startDate={startDate}
				duration={duration}
				timeOffset={timeOffset}
				setFullscreen={setFullscreen}
				setPlay={setPlay}
				setVolume={setVolume}
				setSpeed={setSpeed}
				shouldShowDownload={shouldShowDownload}
				showVideoUnavailable={showVideoUnavailable}
				showAudioThumbnail={audioOnly}
				webcamVideoOptions={videoJsOptions}
				screencaptureVideoOptions={videoJsOptions}
				shouldHideCamVid={hideCameraVideo}
				events={events}
				sessionNotes={[]}
				visibleLiveNotes={visibleLiveNotes}
				webCamHasError={webCamHasError}
				setWebCamHasError={setWebCamHasError}
				screenCaptureHasError={screenCaptureHasError}
				setScreenCaptureHasError={setScreenCaptureHasError}
				proctoredMediaContainer={proctoredMediaContainer}
			/>
		</VideoWrapper>
	);
}
